import {
  type StreetCatalog,
  type StreetLocation,
  isCompleteStreetLocation,
  loadStreetCatalog,
} from '@/helper/streetCatalog';
import { useCallback, useEffect, useState } from 'react';

export type StreetService = {
  catalog: StreetCatalog | undefined;
  loading: boolean;
  error: Error | undefined;
  retry: () => void;
};

export function useStreetService(postcode: string, city: string): StreetService {
  const [retryCount, setRetryCount] = useState(0);
  const [result, setResult] = useState<{
    requestKey: string;
    catalog?: StreetCatalog;
    error?: Error;
  }>();

  const retry = useCallback(() => setRetryCount((count) => count + 1), []);
  const location: StreetLocation = { postcode, city };
  const complete = isCompleteStreetLocation(location);
  const requestKey = `${postcode.trim()}|${city.trim().toLocaleLowerCase('de')}|${retryCount}`;

  useEffect(() => {
    let active = true;
    if (!complete) return;

    void loadStreetCatalog({ postcode, city })
      .then((nextCatalog) => {
        if (active) setResult({ requestKey, catalog: nextCatalog });
      })
      .catch((nextError: unknown) => {
        if (!active) return;
        setResult({
          requestKey,
          error: nextError instanceof Error ? nextError : new Error('Failed to load streets'),
        });
      });

    return () => {
      active = false;
    };
  }, [city, complete, postcode, requestKey]);

  const isCurrentResult = complete && result?.requestKey === requestKey;
  return {
    catalog: isCurrentResult ? result.catalog : undefined,
    loading: complete && !isCurrentResult,
    error: isCurrentResult ? result.error : undefined,
    retry,
  };
}
